import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, doc, setDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";

const ROLES = ["resident", "master", "director", "moderator", "lead"];

/** Onboarding desk: invite by email; people provision themselves on first sign-in. */
export default function People() {
  const s = useSession();
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ email: "", name: "", role: "resident", podId: "", clusterId: "" });
  const [bulk, setBulk] = useState("");
  const [preview, setPreview] = useState(null); // [{email, role, podId, clusterId, name, problem}]
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState(null);
  const cohortId = s.profile?.cohortId;

  useEffect(() => onSnapshot(collection(db, "invites"), (snap) =>
    setInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() })))), []);

  useEffect(() => {
    if (!cohortId) return;
    return onSnapshot(query(collection(db, "users"), where("cohortId", "==", cohortId)), (snap) =>
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [cohortId]);

  const invite = async (rec) => {
    const email = rec.email.toLowerCase().trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setMsg(`Not a valid email: ${rec.email}`);
    await setDoc(doc(db, "invites", email), {
      email,
      name: rec.name?.trim() || null,
      role: rec.role,
      cohortId,
      podId: rec.podId?.trim() || null,
      clusterId: rec.clusterId?.trim() || null,
      status: "pending",
      createdBy: s.user.uid,
      createdAt: serverTimestamp(),
    });
  };

  const single = async () => {
    if (!f.email) return setMsg("Email is required.");
    await invite(f);
    setMsg(`Invited ${f.email.toLowerCase().trim()} as ${f.role}. They sign in with Google at myatelieros.com and are provisioned automatically.`);
    setF({ ...f, email: "", name: "" });
  };

  const runBulk = async () => {
    const lines = bulk.split("\n").map((l) => l.trim()).filter(Boolean);
    let n = 0;
    for (const line of lines) {
      const [email, role = "resident", podId = "", clusterId = "", ...nameParts] = line.split(",").map((x) => x.trim());
      if (!email) continue;
      if (!ROLES.includes(role)) { setMsg(`Skipped ${email}: unknown role "${role}"`); continue; }
      await invite({ email, role, podId, clusterId, name: nameParts.join(" ") });
      n++;
    }
    setMsg(`Created ${n} invitation(s). Share one instruction with everyone: sign in with Google at myatelieros.com using this exact email address.`);
    setBulk("");
  };


  // ---------- file import: CSV, Excel, Word ----------
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  const normaliseRows = (rows) => {
    // rows: array of arrays. Detect a header row; otherwise positional:
    // email, role, podId, clusterId, name
    if (!rows.length) return [];
    let header = null;
    const first = rows[0].map((c) => String(c ?? "").toLowerCase().trim());
    if (first.some((c) => c === "email" || c === "e-mail")) {
      header = first;
      rows = rows.slice(1);
    }
    const col = (name, alts, i) => {
      if (!header) return i;
      const idx = header.findIndex((h) => h === name || alts.includes(h));
      return idx === -1 ? null : idx;
    };
    const ci = {
      email: col("email", ["e-mail"], 0),
      role: col("role", [], 1),
      podId: col("podid", ["pod", "pod id"], 2),
      clusterId: col("clusterid", ["cluster", "cluster id"], 3),
      name: col("name", ["full name", "fullname"], 4),
    };
    const seen = new Set();
    return rows
      .map((r) => r.map((c) => String(c ?? "").trim()))
      .filter((r) => r.some(Boolean))
      .map((r) => {
        const rec = {
          email: (ci.email != null ? r[ci.email] : "").toLowerCase(),
          role: (ci.role != null ? r[ci.role] : "").toLowerCase() || "resident",
          podId: ci.podId != null ? r[ci.podId] : "",
          clusterId: ci.clusterId != null ? r[ci.clusterId] : "",
          name: ci.name != null ? r[ci.name] : "",
        };
        // rescue: if the email cell isn't an email, find one anywhere in the row
        if (!EMAIL_RE.test(rec.email)) {
          const found = r.find((c) => EMAIL_RE.test(c.toLowerCase()));
          if (found) rec.email = found.toLowerCase();
        }
        let problem = null;
        if (!EMAIL_RE.test(rec.email)) problem = "no valid email on this row";
        else if (!ROLES.includes(rec.role)) problem = `unknown role \u201C${rec.role}\u201D`;
        else if (seen.has(rec.email)) problem = "duplicate of an earlier row";
        else if (invites.some((i) => i.id === rec.email)) problem = "already invited";
        seen.add(rec.email);
        return { ...rec, problem };
      });
  };

  const onFile = async (file) => {
    if (!file) return;
    setMsg(null); setPreview(null);
    const ext = file.name.toLowerCase().split(".").pop();
    try {
      let rows = [];
      if (ext === "csv" || ext === "txt") {
        const Papa = (await import("papaparse")).default;
        const text = await file.text();
        rows = Papa.parse(text, { skipEmptyLines: true }).data;
      } else if (ext === "xlsx" || ext === "xls") {
        const XLSX = await import("xlsx");
        const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
        rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" });
      } else if (ext === "docx") {
        const mammoth = await import("mammoth/mammoth.browser");
        const { value } = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        rows = value.split("\n").map((l) => l.trim()).filter((l) => l.includes("@"))
          .map((l) => l.split(/[,\t;]/).map((c) => c.trim()));
        if (!rows.length) return setMsg("No email lines found in that Word document. Type one person per line (email, role, pod) — or better, save the list as CSV or Excel.");
      } else {
        return setMsg("Use a .csv, .xlsx, .xls, or .docx file.");
      }
      const parsed = normaliseRows(rows);
      if (!parsed.length) return setMsg("The file parsed but contained no rows.");
      setPreview(parsed);
    } catch (e) {
      setMsg("Could not read that file: " + e.message);
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    const good = preview.filter((r) => !r.problem);
    for (const r of good) await invite(r);
    setImporting(false);
    setMsg(`Created ${good.length} invitation(s)${preview.length - good.length ? `; skipped ${preview.length - good.length} problem row(s)` : ""}. One instruction for everyone: sign in with Google at myatelieros.com using this exact email address.`);
    setPreview(null);
  };

  const downloadTemplate = async (kind) => {
    const header = [["email", "role", "podId", "clusterId", "name"]];
    const example = [["ada@example.com", "resident", "pod-01", "cluster-a", "Ada Obi"],
                     ["sam@studio.com", "master", "", "cluster-a", "Sam Rivera"]];
    if (kind === "csv") {
      const csv = [...header, ...example].map((r) => r.join(",")).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      a.download = "atelier-invitations-template.csv"; a.click();
    } else {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.aoa_to_sheet([...header, ...example]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Invitations");
      XLSX.writeFile(wb, "atelier-invitations-template.xlsx");
    }
  };

  const pending = invites.filter((i) => i.status === "pending");
  const claimed = invites.filter((i) => i.status === "claimed");

  return (
    <div style={{ maxWidth: 960 }}>
      <Eyebrow>People · onboarding desk</Eyebrow>
      <h1 style={{ fontSize: 24, marginTop: 4 }}>Invite by email; the platform does the rest</h1>
      <p style={{ color: "var(--graphite)", maxWidth: "70ch" }}>
        An invitation is matched to the exact email address on first Google sign-in: the person's record,
        role, pod, and permissions are created automatically. No UIDs, no scripts, no waiting. Invitations
        are senior-staff actions; matching is case-insensitive but address-exact.
      </p>
      {msg && <div className="card" style={{ margin: "12px 0" }}>{msg}</div>}

      {s.isSenior && (
        <>
          <div className="card" style={{ marginTop: 12 }}>
            <Eyebrow>Invite one person</Eyebrow>
            <div className="row wrapflex" style={{ gap: 8, marginTop: 8 }}>
              <input type="text" placeholder="email@example.com" style={{ width: 230 }} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
              <input type="text" placeholder="Full name (optional)" style={{ width: 190 }} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
              <input type="text" placeholder="podId" style={{ width: 110 }} value={f.podId} onChange={(e) => setF({ ...f, podId: e.target.value })} />
              <input type="text" placeholder="clusterId" style={{ width: 110 }} value={f.clusterId} onChange={(e) => setF({ ...f, clusterId: e.target.value })} />
              <button className="btn" onClick={single}>Invite</button>
            </div>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <Eyebrow>Bulk invite — one per line: email, role, podId, clusterId, name</Eyebrow>
            <textarea rows={5} value={bulk} onChange={(e) => setBulk(e.target.value)}
              placeholder={"ada@example.com, resident, pod-01, cluster-a, Ada Obi\nsam@studio.com, master, , cluster-a, Sam Rivera"} style={{ marginTop: 8 }} />
            <button className="btn" style={{ marginTop: 8 }} onClick={runBulk}>Create invitations</button>
          </div>
        </>
      )}

      {s.isSenior && (
        <div className="card" style={{ marginTop: 12 }}>
          <Eyebrow>Import a list — CSV, Excel, or Word</Eyebrow>
          <p style={{ fontSize: 13, color: "var(--graphite)", margin: "6px 0 8px" }}>
            Columns (header row optional): <span className="mono" style={{ fontSize: 12 }}>email, role, podId, clusterId, name</span>.
            Only email is required; role defaults to resident. In Word, put one person per line separated by
            commas — for actual tables, save as CSV or Excel instead.
          </p>
          <div className="row wrapflex" style={{ gap: 8 }}>
            <label className="btn" style={{ display: "inline-block" }}>
              Choose file
              <input type="file" accept=".csv,.txt,.xlsx,.xls,.docx" style={{ display: "none" }}
                onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            <button className="btn ghost" onClick={() => downloadTemplate("csv")}>CSV template</button>
            <button className="btn ghost" onClick={() => downloadTemplate("xlsx")}>Excel template</button>
          </div>

          {preview && (
            <div style={{ marginTop: 12, borderTop: "1px solid var(--rule)", paddingTop: 12 }}>
              <Eyebrow>Preview — nothing is created until you confirm</Eyebrow>
              <table style={{ marginTop: 8 }}>
                <thead><tr><th>Email</th><th>Role</th><th>Pod</th><th>Cluster</th><th>Name</th><th>Status</th></tr></thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 13 }}>{r.email || "—"}</td><td>{r.role}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{r.podId || "—"}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{r.clusterId || "—"}</td>
                      <td style={{ fontSize: 13 }}>{r.name || "—"}</td>
                      <td>{r.problem ? <span className="fail" style={{ fontSize: 12 }}>{r.problem}</span> : <span className="pass" style={{ fontSize: 12 }}>ready</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <button className="btn" disabled={importing || !preview.some((r) => !r.problem)} onClick={confirmImport}>
                  {importing ? "Creating\u2026" : `Create ${preview.filter((r) => !r.problem).length} invitation(s)`}
                </button>
                <button className="btn ghost" onClick={() => setPreview(null)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginTop: 12 }}>
        <Eyebrow>Pending invitations ({pending.length})</Eyebrow>
        <table style={{ marginTop: 8 }}>
          <thead><tr><th>Email</th><th>Role</th><th>Pod</th><th>Cluster</th><th></th></tr></thead>
          <tbody>
            {pending.map((i) => (
              <tr key={i.id}>
                <td>{i.email}</td><td>{i.role}</td><td className="mono" style={{ fontSize: 12 }}>{i.podId ?? "—"}</td>
                <td className="mono" style={{ fontSize: 12 }}>{i.clusterId ?? "—"}</td>
                <td>{s.isSenior && <button className="btn ghost" onClick={() => deleteDoc(doc(db, "invites", i.id))}>Revoke</button>}</td>
              </tr>
            ))}
            {!pending.length && <tr><td colSpan={5} style={{ color: "var(--graphite)" }}>None outstanding.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <Eyebrow>People on {cohortId} ({users.length})</Eyebrow>
        <table style={{ marginTop: 8 }}>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Pod</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td style={{ fontSize: 13 }}>{u.email}</td>
                <td>
                  {s.isSenior ? (
                    <select value={u.role} onChange={(e) => updateDoc(doc(db, "users", u.id), { role: e.target.value })}>
                      {ROLES.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  ) : u.role}
                </td>
                <td>
                  {s.isSenior ? (
                    <input type="text" defaultValue={u.podId ?? ""} style={{ width: 100 }}
                      onBlur={(e) => e.target.value !== (u.podId ?? "") && updateDoc(doc(db, "users", u.id), { podId: e.target.value || null })} />
                  ) : <span className="mono" style={{ fontSize: 12 }}>{u.podId ?? "—"}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: "var(--graphite)", marginTop: 8 }}>
          Role changes take effect on the person's next sign-in (the role rides on their token). Claimed
          invitations: {claimed.length}.
        </p>
      </div>
    </div>
  );
}
